import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridEditMode,
  GridEditModes,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from '@microservices/wiskey-react-table-component'
import { Grid, SxProps, Theme } from '@mui/material'

import { generateDataGridActionsColumnForContext } from '@pages/ContextCreateOrEdit/helpers'

import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { UseEditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { generateConfigSelectColumn } from '@helpers'
import { CONFIG_MENU_OPTION, FLEXIBLE } from '@constants'
import { ObjectDataRecord } from '@types'

type TableWithActionsProps<T> = {
  columns: GridColDef<any>[]
  rows: T[]
  onSave?: (id: number) => void
  onCancel?: (id: number) => void
  onDelete?: (id: number) => void
  onCopy?: (data: any) => void
  onEdit?: (id: number | null, focus?: boolean) => void
  onAdd?: (data: any) => void
  loading?: boolean
  tableHeight?: number
  skipDeleteModal?: boolean
  sx?: SxProps<Theme>
  sxGrid?: SxProps<Theme>
  headerNameActions?: string
  minWidthActions?: number
  disabledDelete?: boolean
  editMode?: GridEditMode
  editRowId?: string | number | null
  configMenu?: UseEditConfigMenu
}

export const TableWithActions = <T extends { id: string | number }>({
  columns,
  rows,
  onSave,
  onCancel,
  onDelete,
  onCopy,
  onEdit,
  onAdd,
  loading,
  tableHeight,
  skipDeleteModal,
  sx,
  sxGrid,
  headerNameActions,
  minWidthActions,
  disabledDelete,
  editMode,
  editRowId,
  configMenu,
}: TableWithActionsProps<T>) => {
  const { t } = useTranslation()
  const apiRef = useGridApiRef()

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [currentRowId, setCurrentRowId] = useState<number>()
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const handleStopRowEditing: GridEventListener<'rowEditStop'> = ({ reason, row: oldRow }) => {
    if (reason === GridRowEditStopReasons.enterKeyDown) {
      setViewRowModeModel(oldRow.id)
      onSave?.(oldRow.id)
    }

    if (
      reason === GridRowEditStopReasons.escapeKeyDown ||
      reason === GridRowEditStopReasons.rowFocusOut
    ) {
      setViewRowModeModel(oldRow.id)
      onCancel?.(oldRow.id)
    }

    onEdit?.(null)
  }

  const handleProcessRowUpdate = (
    _newRow: GridRowModel<ObjectDataRecord>,
    oldRow: GridRowModel<ObjectDataRecord>
  ) => oldRow

  const setViewRowModeModel = (id: string | number) =>
    setRowModesModel({
      [id]: {
        mode: GridRowModes.View,
        ignoreModifications: true,
      },
    })

  const handleSave = (params: GridCellParams<any, T>) => {
    onSave?.(params.row.id)
    setViewRowModeModel(params.row.id)
    onEdit?.(null)
  }

  const handleCancel = (params: GridCellParams<any, T>) => {
    setViewRowModeModel(params.row.id)
    onEdit?.(null)
    onCancel?.(params.row.id)
  }

  const handleCopy = (params: GridCellParams<any, T>) => {
    const { id, title } = params.row
    onCopy?.({ id, title })
  }

  const handleEdit = (params: GridCellParams<any, T>, event: any) => {
    if (editMode === GridEditModes.Row) {
      setRowModesModel({
        [params.row.id]: {
          mode: GridRowModes.Edit,
          fieldToFocus: 'value',
        },
      })
      onEdit?.(params.row.id, true)

      return
    }

    onEdit?.(params.row.id)
  }

  const handleClickDelete = (params: GridCellParams<any, T>) => {
    const id = params.row.id

    if (id) {
      if (skipDeleteModal) {
        onDelete?.(id)

        return
      }

      setShowDeleteModal(true)
      setCurrentRowId(id)
    }
  }

  const handleDelete = () => {
    if (currentRowId) {
      onDelete?.(currentRowId)
      setShowDeleteModal(false)
    }
  }

  const handleAdd = (params: GridCellParams<any, T>) => onAdd?.(params.row.id)

  const handleClose = () => setShowDeleteModal(false)

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      configMenu?.configMenuOption &&
        configMenu?.configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
        generateConfigSelectColumn({
          selectedConfigId: configMenu?.selectedConfigId,
          onSelectRow: configMenu?.handleSelectConfigId,
        }),
      generateDataGridActionsColumnForContext({
        headerName: headerNameActions,
        minWidth: minWidthActions,
        disabledDelete,
        editRowId,
        onAdd: onAdd ? handleAdd : undefined,
        onEdit: onEdit ? handleEdit : undefined,
        onCopy: onCopy ? handleCopy : undefined,
        onDelete: onDelete ? handleClickDelete : undefined,
        onSave: onSave ? handleSave : undefined,
        onCancel: onCancel ? handleCancel : undefined,
      }),
    ],
    [columns, disabledDelete, editRowId, configMenu?.selectedConfigId, configMenu?.configMenuOption]
  )

  return (
    <Grid
      container
      sx={{
        ...FLEXIBLE,
        ...sx,
      }}
    >
      <EditConfigScriptDialog />
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleClose}
          onConfirm={handleDelete}
        />
      )}
      <Grid container item sx={FLEXIBLE}>
        <DataGrid
          {...(editMode && { editMode })}
          hideFooter
          apiRef={apiRef}
          columns={columnsWithActions}
          loading={loading}
          processRowUpdate={handleProcessRowUpdate}
          rows={rows}
          tableHeight={tableHeight}
          sx={{
            ...sxGrid,
            // borderRadius: 0
          }}
          rowModesModel={rowModesModel}
          //onRowEditStart={handleStartRowEditing}
          onRowEditStop={handleStopRowEditing}
        />
      </Grid>
      <ExportActionsPanel />
    </Grid>
  )
}
